import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { ServiceDetailDto, ServiceSummaryDto } from '@/api/types';

// First-class Services state. The list endpoint returns names; per-name detail is fetched
// on demand and cached so reopening a service in the editor doesn't re-roundtrip.
interface ServicesState {
  list: ServiceSummaryDto[];
  detail: Record<string, ServiceDetailDto | undefined>;
  loaded: boolean;
  loading: boolean;
  error: string | null;
}

export const useServicesStore = defineStore('services', {
  state: (): ServicesState => ({
    list: [],
    detail: {},
    loaded: false,
    loading: false,
    error: null,
  }),
  actions: {
    async load(force = false) {
      if (this.loaded && !force) return;
      this.loading = true;
      this.error = null;
      try {
        this.list = await adminApi.listServices();
        this.loaded = true;
      } catch (e) {
        this.error = (e as Error).message;
      } finally {
        this.loading = false;
      }
    },
    async loadDetail(name: string, force = false): Promise<ServiceDetailDto | null> {
      if (!force && this.detail[name]) {
        return this.detail[name]!;
      }
      try {
        const dto = await adminApi.getService(name);
        this.detail = { ...this.detail, [name]: dto };
        return dto;
      } catch (e) {
        this.error = (e as Error).message;
        return null;
      }
    },
    async add(name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.addService({ name });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async remove(name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.removeService(name);
        const next = { ...this.detail };
        delete next[name];
        this.detail = next;
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
  },
});
